import React from "react";
import type { OrderTodo, TodoStatus } from "../../../../domain/types/todo";
import { Clock, User, AlertTriangle, Lock, Calendar } from "lucide-react";
import { ORDER_PRIORITY_LABELS, type OrderPriority } from "../../../../domain/types/todo";

interface TodoKanbanCardProps {
  todo: OrderTodo;
  onClick: (todo: OrderTodo) => void;
  isDragging?: boolean;
}

// 카드 타이틀 색상 (관리자 홈 대시보드와 동일)
// 작업전/대기중: 기본색, 작업중/완료: 파란색
const getTitleColor = (status: TodoStatus): string => {
  switch (status) {
    case "IN_PROGRESS":
    case "COMPLETED":
      return "#4285F4"; // 파란색
    case "PENDING":
    case "WAITING":
    default:
      return "#111827"; // 기본 검정색
  }
};

export const TodoKanbanCard: React.FC<TodoKanbanCardProps> = ({
  todo,
  onClick,
  isDragging = false,
}) => {
  const formatMinutes = (minutes: number): string => {
    if (minutes < 60) return `${minutes}분`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}시간 ${mins}분` : `${hours}시간`;
  };

  const isOverdue = todo.order?.dueDate
    ? new Date(todo.order.dueDate) < new Date()
    : false;

  const isDueSoon = todo.order?.dueDate
    ? new Date(todo.order.dueDate).getTime() - new Date().getTime() <
      24 * 60 * 60 * 1000
    : false;

  const hasBlockingDependency = todo.dependencies.some(
    (d) => d.dependencyType === "BLOCKING" && d.dependsOnTodoStatus !== "COMPLETED"
  );

  const getPriorityColor = (priority: OrderPriority) => {
    switch (priority) {
      case "HIGHEST":
        return "bg-red-100 text-red-700";
      case "HIGH":
        return "bg-orange-100 text-orange-700";
      case "MEDIUM":
        return "bg-yellow-100 text-yellow-700";
      case "LOW":
        return "bg-green-100 text-green-700";
      case "LOWEST":
        return "bg-gray-100 text-gray-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div
      onClick={() => onClick(todo)}
      className={`bg-white rounded-[0.625rem] p-3 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow w-full ${
        isDragging ? "border-2 border-blue-300 shadow-lg" : ""
      } ${hasBlockingDependency ? "ring-1 ring-red-300" : ""}`}
    >
      {/* Blocking indicator */}
      {hasBlockingDependency && (
        <div className="flex items-center gap-1 text-red-600 text-xs mb-2">
          <Lock size={12} />
          <span>선행 작업 대기중</span>
        </div>
      )}

      {/* Task Name */}
      <h4
        className="text-sm font-semibold truncate text-left mb-1"
        style={{ color: getTitleColor(todo.status) }}
      >
        {todo.name}
      </h4>

      {/* Order Info */}
      {todo.order && (
        <div className="text-xs text-gray-600 space-y-1 mb-2">
          <div className="flex justify-between">
            <span className="text-gray-500">주문번호</span>
            <span className="font-medium">{todo.order.orderNumber}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">주문명</span>
            <span className="truncate ml-2">{todo.order.name}</span>
          </div>
        </div>
      )}

      {/* Meta Info */}
      <div className="text-xs text-gray-600 space-y-1">
        {/* Priority */}
        {todo.order?.priority && (
          <div className="flex justify-between">
            <span className="text-gray-500">우선순위</span>
            <span className={`px-1.5 py-0.5 rounded text-xs ${getPriorityColor(todo.order.priority)}`}>
              {ORDER_PRIORITY_LABELS[todo.order.priority]}
            </span>
          </div>
        )}

        {/* Estimated Time */}
        <div className="flex justify-between">
          <span className="text-gray-500">예상 시간</span>
          <span className="font-medium">{formatMinutes(todo.estimatedMinutes)}</span>
        </div>

        {/* Due Date */}
        {todo.order?.dueDate && (
          <div className="flex justify-between">
            <span className="text-gray-500">마감일</span>
            <span
              className={`font-medium ${
                isOverdue
                  ? "text-red-600"
                  : isDueSoon
                  ? "text-orange-600"
                  : ""
              }`}
            >
              {new Date(todo.order.dueDate).toLocaleDateString("ko-KR", {
                month: "short",
                day: "numeric",
              })}
              {isOverdue && " (초과)"}
            </span>
          </div>
        )}
      </div>

      {/* Assignees */}
      {todo.assignees.length > 0 && (
        <div className="flex items-center gap-1 pt-2 mt-2 border-t border-gray-100">
          <div className="flex -space-x-1">
            {todo.assignees.slice(0, 3).map((assignee) => (
              <div
                key={assignee.id}
                className="w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center border border-white"
                title={assignee.userName}
              >
                {assignee.userName?.charAt(0) || "?"}
              </div>
            ))}
            {todo.assignees.length > 3 && (
              <div className="w-5 h-5 rounded-full bg-gray-300 text-gray-700 text-xs flex items-center justify-center border border-white">
                +{todo.assignees.length - 3}
              </div>
            )}
          </div>
          <span className="text-xs text-gray-500 ml-1">
            {todo.assignees[0]?.userName}
            {todo.assignees.length > 1 && ` 외 ${todo.assignees.length - 1}명`}
          </span>
        </div>
      )}

      {/* Notes indicator */}
      {todo.notes.length > 0 && (
        <div className="text-xs text-gray-400 mt-2">
          메모 {todo.notes.length}개
        </div>
      )}
    </div>
  );
};

export default TodoKanbanCard;
